<script setup>
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import {computed, ref, watch} from "vue";
import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import SlideUpTransition from "@/components/transitions/SlideUpTransition.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import useActionSettings from "@/composables/useActionSettings";

const trackCrawlerTime = ref(0);
const showTimingsOnActions = ref(0);
const minutesAssigned = ref(0);
const totalTime = ref(0);

const showSaveChanges = ref(false);

const {minuteOptions, saveSettingsMutation, settingsQuery} = useActionSettings();
const { isLoading, isFetching } = settingsQuery;
const { isPending: saving } = saveSettingsMutation;

const settings = computed(() => settingsQuery.data.value);

watch(settings, () => {
  totalTime.value = settings.value?.total_time;
  minutesAssigned.value = settings.value?.minutes_assigned || 60;
  showTimingsOnActions.value = settings.value?.show_action_timings || 0;
  trackCrawlerTime.value = settings.value?.track_crawler_time || 0;
}, {
  immediate: true,
  deep: true,
})

watch(
  [minutesAssigned, showTimingsOnActions, trackCrawlerTime],
  ([newMinutes, newTimings, newCrawlerTime]) => {
    if (isLoading.value || isFetching.value) {
      return;
    }

    const isModified =
      parseInt(newMinutes) !== settings.value.minutes_assigned ||
      parseInt(newTimings) !== settings.value.show_action_timings ||
      parseInt(newCrawlerTime) !== settings.value.track_crawler_time;

    showSaveChanges.value = isModified;
  },
);

const handleSave = () => {
  saveSettingsMutation.mutate({
    minutes_assigned: minutesAssigned.value,
    show_action_timings: showTimingsOnActions.value,
    track_crawler_time: trackCrawlerTime.value,
  }, undefined, undefined, () => {
    showSaveChanges.value = false;
  })
}

const monthsToComplete = computed(() => {
  if (totalTime.value > 0 && minutesAssigned.value > 0) {
    return Math.ceil(totalTime.value / minutesAssigned.value);
  }
  return 0;
});
</script>

<template>
  <div>
    <LoadingBlock v-if="isLoading" />
    <div
      v-else
      class="tw-bg-white tw-p-8 tw-rounded-lg tw-flex tw-flex-col tw-gap-y-8 tw-w-full tw-transition-all tw-duration-300"
      :class="[
        showSaveChanges ? 'tw-mb-[122px]' : 'tw-mb-0'
      ]"
    >
      <div class="tw-flex tw-flex-col tw-gap-10 tw-pb-12 tw-border-b tw-border-grey-mid">
        <div class="tw-flex tw-justify-between tw-items-center">
          <p class="tw-font-bold tw-text-grey-dark tw-text-2.5xl">
            Hours Per Month
          </p>

          <CustomMultiselect
            v-model="minutesAssigned"
            class="tw-text-left tw-h-12.5 tw-relative tw-w-[232px]"
            :close-on-select="true"
            :options="minuteOptions"
            :searchable="false"
            :can-deselect="false"
          >
            <template #icon>
              <svg-vue
                icon="icons/clock"
                class="tw-w-[18px] tw-h-[18px] tw-text-grey"
              />
            </template>
          </CustomMultiselect>
        </div>

        <div class="tw-flex tw-flex-col tw-gap-8 tw-text-lg">
          <p class="tw-text-grey-dark tw-leading-snug">
            How many hours can you commit to your SEO per month?
          </p>
          <p class="tw-text-grey-dark tw-leading-snug">
            Based on this selection, it would take an estimated
            <span class="tw-font-bold">
              {{ monthsToComplete }} months
            </span>
            to complete your website actions.
          </p>
        </div>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-10 tw-pb-12 tw-border-b tw-border-grey-mid">
        <div class="tw-flex tw-justify-between tw-items-center">
          <p class="tw-font-bold tw-text-grey-dark tw-text-2.5xl">
            Export Completed Action Timings
          </p>

          <CustomMultiselect
            v-model="showTimingsOnActions"
            class="tw-text-left tw-h-12.5"
            :close-on-select="true"
            :options="{
              0: 'No, don\'t show',
              1: 'Yes, show timings'
            }"
            :searchable="false"
            :can-deselect="false"
          />
        </div>

        <div class="tw-flex tw-flex-col tw-gap-8 tw-text-lg">
          <p class="tw-text-grey-dark tw-leading-snug">
            Do you want to show the time that an action has taken to complete
            on the completed action export?
          </p>
        </div>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-10">
        <div class="tw-flex tw-justify-between tw-items-center">
          <p class="tw-font-bold tw-text-grey-dark tw-text-2.5xl">
            Report on All Completed Actions?
          </p>

          <CustomMultiselect
            v-model="trackCrawlerTime"
            class="tw-text-left tw-h-12.5"
            :close-on-select="true"
            :options="{
              0: 'No, don\'t include',
              1: 'Yes, please include'
            }"
            :searchable="false"
            :can-deselect="false"
          />
        </div>

        <div class="tw-flex tw-flex-col tw-gap-8 tw-text-lg">
          <p class="tw-text-grey-dark tw-leading-snug">
            During a website re-crawl, we may find tasks that have been finished but not manually marked as ‘complete’
            in
            the <br> platform.
            To prevent these actions from being included in your completed report or counting towards your monthly time
            <br> limit, please choose
            <span class="tw-font-bold">“No, Don’t Include.”</span>
          </p>
        </div>
      </div>
    </div>
    <SlideUpTransition>
      <div
        v-if="showSaveChanges"
        class="tw-flex tw-justify-between tw-w-full tw-border-t-2 tw-border-grey-mid tw-py-8 tw-px-16 tw-bg-white tw-fixed tw-bottom-0 tw-left-0 tw-transition-transform tw-ease-in-out tw-duration-300"
      >
        <DefaultButton
          :disabled="saving"
          class="tw-w-56 ml-auto tw-py-4"
          @click="handleSave"
        >
          <div class="tw-flex tw-gap-4 tw-items-center">
            <svg-vue
              v-if="saving"
              icon="icons/loading"
              class="tw-w-6 tw-h-6"
            />
            <span>
              Save Changes
            </span>
          </div>
        </DefaultButton>
      </div>
    </SlideUpTransition>
  </div>
</template>
